import React from 'react'
import styled, { css, StyledProps } from 'styled-components'

type ThemedProps = StyledProps<{}>
type SVGIcon = React.FunctionComponent<React.SVGProps<SVGSVGElement> & { title?: string }>

const buttonStyle = css`
  background-color: ${(props: ThemedProps) => props.theme.color.primary5};
  border-radius: 8px;
  height: 48px;
  display: inline-flex;
  min-width: 48px;
  border: none;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 0 14px;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  transition: 0.2s background-color, 0.2s color;
  color: ${(props: ThemedProps) => props.theme.color.onPrimaryDark};

  &:hover {
    background-color: ${(props: ThemedProps) => props.theme.color.primary50};
    color: ${(props: ThemedProps) => props.theme.color.primary600};
  }

  &:focus {
    outline: none;
    box-shadow: ${(props: ThemedProps) => props.theme.shadow.focus};
  }
`

const Button = styled.button<{ color?: string }>`
  ${buttonStyle}
  svg {
    fill: ${props => (props.color ? props.color : props.theme.color.primary600)};
  }
`

const Wrapper = styled.div<{ color?: string }>`
  ${buttonStyle}
  svg {
    fill: ${props => (props.color ? props.color : props.theme.color.primary600)};
  }
`

const Text = styled.span`
  margin-left: 10px;
`

export type IconButtonProps = {
  to?: string | { pathname: string; state: { [key: string]: any } }
  onClick?: () => void
  onBlur?: () => void
  Icon: SVGIcon
  iconColor?: string
  className?: string
  doNotWrapText?: boolean
  hideTextOnMobile?: boolean
  'aria-label'?: string
  title?: string
  id?: string
  button?: boolean
}

const ButtonText = styled.span<StyledProps<{ hideTextOnMobile?: boolean }>>`
  ${(props: StyledProps<{ hideTextOnMobile?: boolean }>) => props.theme.media.mobile`
    display: ${props.hideTextOnMobile ? 'none' : 'inline'}
  `}
`

export const IconButton: React.FC<IconButtonProps> = ({
  button = true,
  children,
  onClick,
  Icon,
  className,
  iconColor,
  doNotWrapText,
  hideTextOnMobile,
  ...rest
}) => {
  const childrenNode = doNotWrapText ? children : !!children && <Text>{children}</Text>

  if (button) {
    return (
      <Button {...rest} onClick={onClick} type={'button'} className={className} color={iconColor}>
        <Icon />
        <ButtonText hideTextOnMobile={hideTextOnMobile}>{childrenNode}</ButtonText>
      </Button>
    )
  } else {
    return (
      <Wrapper {...rest} onClick={onClick} className={className} color={iconColor}>
        <Icon />
        <ButtonText hideTextOnMobile={hideTextOnMobile}>{childrenNode}</ButtonText>
      </Wrapper>
    )
  }
}
